import React, { useEffect, useState, useMemo } from 'react';
import BaseCard from '../../common/layout/BaseCard';
import { Button } from '../../common/ui/Button';
import { ModalDialog } from '../../common/ui/ModalDialog';
import { Dropdown } from '../../common/ui/Listbox';
import Tooltip from '../../common/ui/Tooltip';
import { DataTable, DataTableColumn } from '../../common/display/DataTable';
import Avatar from '../../common/display/Avatar';
import Pill from '../../common/display/Pill';
import { supabase } from '../../../lib/supabase';
import { captureAppException } from '../../../utils/errorCaptureUtils';
import { captureCustomEvent } from '../../../utils/posthogUtils';
import { useAuth } from '../../../lib/AuthContext';
import { PlusIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { CommunityMemberRole } from '../types';

interface ManageCommunityMembersProps {
  communityId: string;
}

interface DisplayMember {
  community_member_id: string;
  user_id: string;
  role: CommunityMemberRole;
  created_at: string;
  type: 'member' | 'invite';
  users: {
    first_name: string | null;
    last_name: string | null;
    email: string | null;
    profile_image_url: string | null;
  } | null;
  email?: string;
  discord_connected?: boolean;
  discord_username?: string;
}

const ROLE_OPTIONS = [
  { id: 'member', text: 'Member' },
  { id: 'banned', text: 'Banned' }
];

const BULK_ACTIONS = [
  { id: 'change_role', text: 'Change Role' },
  { id: 'remove_members', text: 'Remove Members' },
  { id: 'export_csv', text: 'Export CSV' }
];

const STATUS_MAP = {
  member: { text: 'Active', className: 'bg-green-100 text-green-800 text-xs' },
  pending: { text: 'Pending', className: 'bg-yellow-100 text-yellow-800 text-xs' },
  invited: { text: 'Invited', className: 'bg-blue-100 text-blue-800 text-xs' },
  banned: { text: 'Banned', className: 'bg-red-100 text-red-800 text-xs' }
};

export const ManageCommunityMembers: React.FC<ManageCommunityMembersProps> = ({ communityId }) => {
  const { user } = useAuth();
  const [members, setMembers] = useState<DisplayMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [updating, setUpdating] = useState<string | null>(null);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  const [exportStatus, setExportStatus] = useState<'idle' | 'preparing' | 'ready'>('idle');
  const [bulkAction, setBulkAction] = useState<{ type: 'change_role' | 'remove_members' | 'export_csv' | null; roleChange?: string }>({ type: null });
  const [confirmingAction, setConfirmingAction] = useState<{
    memberIds: Set<string>;
    action: 'remove' | 'change_role';
    memberName?: string;
    newRole?: string;
  } | null>(null);
  const [tableState, setTableState] = useState({
    currentPage: 1,
    itemsPerPage: 10,
    sortField: 'name',
    sortDirection: 'asc' as 'asc' | 'desc',
    selectedItems: new Set<string>()
  });

  const currentUserMember = members.find(m => m.user_id === user?.id && m.type === 'member');
  const canManageMembers = currentUserMember?.role === 'member';

  const getUserFullName = (member: DisplayMember) => {
    if (member.type === 'invite') return member.email || 'Unknown';
    const firstName = member.users?.first_name || '';
    const lastName = member.users?.last_name || '';
    return `${lastName}, ${firstName}`.trim() || 'Unknown User';
  };

  const canModifyMember = (member: DisplayMember) => member.user_id !== user?.id && canManageMembers;

  const getActionRestriction = (member: DisplayMember) => {
    if (member.user_id === user?.id) return 'You cannot modify your own membership.';
    if (!canManageMembers) return 'You do not have permission to manage community members.';
    return null;
  };

  const getBulkActionRestriction = (action: string) => {
    if (!canManageMembers) return 'You do not have permission to manage community members.';
    const selectedMembers = members.filter(m => tableState.selectedItems.has(m.community_member_id));
    if (selectedMembers.find(m => m.user_id === user?.id)) {
      return `You cannot ${action} yourself using bulk actions.`;
    }
    return null;
  };

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchDiscordStatus = async (): Promise<{ [userId: string]: { isConnected: boolean; discordUsername?: string } }> => {
    try {
      const { data, error } = await supabase.functions.invoke('discord-api-helper', {
        body: { 
          action: 'get_community_members_discord_status',
          payload: { communityId }
        },
      });

      if (error) {
        console.warn('Failed to fetch Discord status:', error);
        // If it's a permission error, don't show Discord status for anyone
        if (error.message && error.message.includes('permission')) {
          console.warn('User does not have permission to view Discord status');
        }
        return {};
      }

      return data || {};
    } catch (err) {
      console.warn('Failed to fetch Discord status:', err);
      return {};
    }
  };

  const fetchMembers = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error } = await supabase
        .from('community_members')
        .select(`user_id, role, created_at, users (first_name, last_name, email, profile_image_url)`)
        .eq('community_id', communityId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const processedMembers = (data || []).map((row: any) => ({
        community_member_id: `${row.user_id}_${communityId}`,
        user_id: row.user_id,
        role: row.role,
        created_at: row.created_at,
        type: 'member' as const,
        users: row.users
      }));

      // Fetch Discord status for all members
      const discordStatus = await fetchDiscordStatus();

      // Add Discord connection status and usernames to members
      const membersWithDiscord = processedMembers.map(member => ({
        ...member,
        discord_connected: discordStatus[member.user_id]?.isConnected || false,
        discord_username: discordStatus[member.user_id]?.discordUsername
      }));

      setMembers(membersWithDiscord);
    } catch (error) {
      captureAppException(error, { 
        tags: { 
          context: 'manage_community_members', 
          community_id: communityId, 
          action: 'fetch_members' 
        } 
      });
      setError('Failed to fetch community members. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMembers(); }, [communityId]);
  useEffect(() => { setError(null); }, [tableState.selectedItems, bulkAction.type]);

  const updateMemberRole = async (memberId: string, newRole: CommunityMemberRole) => {
    setUpdating(memberId);
    
    captureCustomEvent('community_member_role_updated', {
      eventProperties: {
        community_id: communityId,
        member_id: memberId,
        new_role: newRole,
        previous_role: members.find(m => m.community_member_id === memberId)?.role
      }
    });
    
    try {
      const member = members.find(m => m.community_member_id === memberId);
      if (!member) throw new Error('Member not found');

      const { error } = await supabase
        .from('community_members')
        .update({ role: newRole })
        .eq('community_id', communityId)
        .eq('user_id', member.user_id);

      if (error) throw error;

      setMembers(members => members.map(m => 
        m.community_member_id === memberId ? { ...m, role: newRole } : m
      ));
      setConfirmingAction(null);
    } catch (error) {
      captureAppException(error, { 
        tags: { 
          context: 'manage_community_members', 
          community_id: communityId, 
          user_id: memberId, 
          action: 'update_role' 
        } 
      });
      setError('Failed to update member role. Please try again later.');
    } finally {
      setUpdating(null);
    }
  };

  const removeMember = async (memberId: string) => {
    setUpdating(memberId);
    
    captureCustomEvent('community_member_removed', {
      eventProperties: {
        community_id: communityId,
        member_id: memberId,
        member_role: members.find(m => m.community_member_id === memberId)?.role
      }
    });
    
    try {
      const member = members.find(m => m.community_member_id === memberId);
      if (!member) throw new Error('Member not found');

      const { error } = await supabase
        .from('community_members')
        .delete()
        .eq('community_id', communityId)
        .eq('user_id', member.user_id);

      if (error) throw error;

      setMembers(members => members.filter(m => m.community_member_id !== memberId));
      setConfirmingAction(null);
    } catch (error) {
      captureAppException(error, { 
        tags: { 
          context: 'manage_community_members', 
          community_id: communityId, 
          user_id: memberId, 
          action: 'remove_member' 
        } 
      });
      setError('Failed to remove member. Please try again later.');
    } finally {
      setUpdating(null);
    }
  };

  const exportSelectedMembersToCSV = async () => {
    try {
      setExportStatus('preparing');
      const selectedMembers = members.filter(m => tableState.selectedItems.has(m.community_member_id));
      
      captureCustomEvent('community_members_exported', {
        eventProperties: {
          community_id: communityId,
          export_count: selectedMembers.length,
          export_format: 'csv'
        }
      });
      
      const csvData = [
        ['First Name', 'Last Name', 'Email', 'Role', 'Status', 'Joined Date'],
        ...selectedMembers.map(member => [
          member.users?.first_name || '',
          member.users?.last_name || '',
          member.users?.email || member.email || '',
          member.role,
          STATUS_MAP[member.role]?.text || 'Unknown',
          new Date(member.created_at).toLocaleDateString()
        ])
      ];

      const csvContent = csvData.map(row => 
        row.map(cell => 
          typeof cell === 'string' && /[",\n]/.test(cell) ? `"${cell.replace(/"/g, '""')}"` : cell
        ).join(',')
      ).join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      
      link.href = url;
      link.download = `community-members-export-${new Date().toISOString().slice(0, 10)}.csv`;
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setExportStatus('ready');
      setTimeout(() => {
        setExportStatus('idle');
        setBulkAction({ type: null });
      }, 2000);
    } catch (err) {
      captureAppException(err, { tags: { context: 'manage_community_members', action: 'export_csv' }});
      setError('Failed to export members to CSV');
      setExportStatus('idle');
    }
  };

  const handleBulkActionExecute = async (actionId: string, selectedItems: Set<string>) => {
    if (actionId === 'export_csv') {
      await exportSelectedMembersToCSV();
      return;
    }

    const restriction = actionId === 'remove_members' 
      ? getBulkActionRestriction('remove') 
      : getBulkActionRestriction('change roles of');
    
    if (restriction) {
      setError(restriction);
      return;
    }
    
    setConfirmingAction({
      memberIds: selectedItems,
      action: actionId === 'remove_members' ? 'remove' : 'change_role',
      newRole: bulkAction.roleChange
    });
  };

  const confirmBulkAction = async () => {
    if (!confirmingAction) return;
    
    try {
      const memberIds = Array.from(confirmingAction.memberIds);
      
      if (confirmingAction.action === 'change_role' && confirmingAction.newRole) {
        await Promise.all(memberIds.map(id => updateMemberRole(id, confirmingAction.newRole! as CommunityMemberRole)));
      } else if (confirmingAction.action === 'remove') {
        await Promise.all(memberIds.map(id => removeMember(id)));
      }
      
      setTableState(prev => ({ ...prev, selectedItems: new Set() }));
      setBulkAction({ type: null });
      await fetchMembers();
    } catch (err) {
      captureAppException(err);
      setError('Failed to perform bulk action');
    }
  };

  const filteredMembers = useMemo(() => {
    if (!searchTerm) return members;
    const searchStr = searchTerm.toLowerCase();
    return members.filter(member => 
      [getUserFullName(member), member.users?.email || member.email || '', member.role]
        .join(' ').toLowerCase().includes(searchStr)
    );
  }, [members, searchTerm]);

  const sortedMembers = useMemo(() => {
    return [...filteredMembers].sort((a, b) => {
      let compareValue = 0;
      switch (tableState.sortField) {
        case 'name': compareValue = getUserFullName(a).localeCompare(getUserFullName(b)); break;
        case 'role': compareValue = a.role.localeCompare(b.role); break;
        case 'created_at': compareValue = new Date(a.created_at).getTime() - new Date(b.created_at).getTime(); break;
      }
      return tableState.sortDirection === 'asc' ? compareValue : -compareValue;
    });
  }, [filteredMembers, tableState.sortField, tableState.sortDirection]);

  const paginatedMembers = useMemo(() => {
    const startIndex = (tableState.currentPage - 1) * tableState.itemsPerPage;
    return sortedMembers.slice(startIndex, startIndex + tableState.itemsPerPage);
  }, [sortedMembers, tableState.currentPage, tableState.itemsPerPage]);

  const updateTableState = (updates: Partial<typeof tableState>) => {
    setTableState(prev => ({ ...prev, ...updates }));
  };

  const isLargeScreen = windowWidth >= 1024;
  const columns: DataTableColumn<DisplayMember>[] = [
    {
      key: 'name',
      label: 'Member',
      sortable: true,
      width: 'minmax(250px,2fr)',
      render: (member) => (
        <div className="flex items-center gap-3">
          <Avatar
            firstName={member.users?.first_name || ''}
            lastName={member.users?.last_name || ''}
            profileImageUrl={member.users?.profile_image_url || undefined}
            size="small"
          />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <p className="font-medium text-gray-900 truncate">{getUserFullName(member)}</p>
              {member.discord_connected && (
                <Tooltip text={member.discord_username ? `Discord: ${member.discord_username}` : 'Discord Connected'}>
                  <img
                    src={`${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/assets/discord-icon.svg`}
                    alt="Discord"
                    className="w-4 h-4"
                  />
                </Tooltip>
              )}
            </div>
            <p className="text-xs text-gray-500 truncate">{member.users?.email || member.email}</p>
          </div>
        </div>
      ),
      mobileRender: (member) => (
        <div className="flex items-start gap-3 flex-1">
          <Avatar
            firstName={member.users?.first_name || ''}
            lastName={member.users?.last_name || ''}
            profileImageUrl={member.users?.profile_image_url || undefined}
            size="medium"
            className="shrink-0"
          />
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <p className="font-medium text-gray-900">{getUserFullName(member)}</p>
              {member.discord_connected && (
                <Tooltip text={member.discord_username ? `Discord: ${member.discord_username}` : 'Discord Connected'}>
                  <img
                    src={`${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/assets/discord-icon.svg`}
                    alt="Discord"
                    className="w-4 h-4 opacity-70"
                  />
                </Tooltip>
              )}
            </div>
            <p className="text-sm text-gray-500">{member.users?.email || member.email}</p>
            <div className="mt-1">
              {(() => {
                const status = STATUS_MAP[member.role] || { text: 'Unknown', className: 'bg-gray-100 text-gray-800 text-xs' };
                return <Pill text={status.text} className={status.className} />;
              })()}
            </div>
          </div>
        </div>
      )
    },
    {
      key: 'role',
      label: 'Role',
      sortable: true,
      width: 'minmax(120px,1fr)',
      render: (member) => {
        const canModify = canModifyMember(member);
        const restrictionReason = getActionRestriction(member);
        
        const roleDropdown = (
          <Dropdown
            options={ROLE_OPTIONS}
            value={member.role}
            onChange={(option) => updateMemberRole(member.community_member_id, option.id as CommunityMemberRole)}
            disabled={updating === member.community_member_id || !canModify}
            size="small"
            className="w-24 lg:w-30"
          />
        );

        return restrictionReason ? <Tooltip text={restrictionReason}>{roleDropdown}</Tooltip> : roleDropdown;
      },
      mobileLabel: 'Role',
      mobileRender: (member) => <span className="text-sm font-medium capitalize">{member.role}</span>
    },
    {
      key: 'status',
      label: 'Status',
      sortable: false,
      width: 'minmax(120px,1fr)',
      render: (member) => {
        const status = STATUS_MAP[member.role] || { text: 'Unknown', className: 'bg-gray-100 text-gray-800 text-xs' };
        return <Pill text={status.text} className={status.className} />;
      },
      mobileLabel: 'Status'
    },
    ...(isLargeScreen ? [{
      key: 'created_at',
      label: 'Joined',
      sortable: true,
      width: 'minmax(120px,1fr)',
      render: (member: DisplayMember) => <span className="text-sm text-gray-500">{new Date(member.created_at).toLocaleDateString()}</span>,
      mobileLabel: 'Joined',
      mobileRender: (member: DisplayMember) => <span className="text-sm text-gray-500">{new Date(member.created_at).toLocaleDateString()}</span>
    }] : []),
    {
      key: 'actions',
      label: 'Actions',
      width: 'minmax(100px,1fr)',
      align: 'center',
      render: (member) => {
        const canModify = canModifyMember(member);
        const restrictionReason = getActionRestriction(member);
        
        const actionButton = (
          <Button
            variant="outline"
            size="small"
            color="danger"
            onClick={() => {
              if (member.role === 'banned') {
                // Unban by changing role to member
                updateMemberRole(member.community_member_id, 'member');
              } else {
                // Remove member
                setConfirmingAction({
                  memberIds: new Set([member.community_member_id]),
                  action: 'remove',
                  memberName: getUserFullName(member)
                });
              }
            }}
            disabled={updating === member.community_member_id || !canModify}
          >
            {member.role === 'banned' ? 'Unban' : member.type === 'invite' ? 'Cancel' : 'Remove'}
          </Button>
        );

        return restrictionReason ? <Tooltip text={restrictionReason}>{actionButton}</Tooltip> : actionButton;
      }
    }
  ];

  const gridCols = isLargeScreen 
    ? "grid-cols-[48px_minmax(250px,2fr)_minmax(120px,1fr)_minmax(120px,1fr)_minmax(120px,1fr)_minmax(100px,1fr)]"
    : "grid-cols-[48px_minmax(250px,2fr)_minmax(120px,1fr)_minmax(120px,1fr)_minmax(100px,1fr)]";

  const totalItems = filteredMembers.length;
  const totalPages = Math.ceil(totalItems / tableState.itemsPerPage);
  const isAllCurrentPageSelected = paginatedMembers.length > 0 && 
    paginatedMembers.every(member => tableState.selectedItems.has(member.community_member_id));
  const isIndeterminate = paginatedMembers.length > 0 && 
    paginatedMembers.some(member => tableState.selectedItems.has(member.community_member_id)) && 
    !isAllCurrentPageSelected;

  if (loading) {
    return (
      <BaseCard>
        <div className="flex items-center justify-center min-h-[200px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-base"></div>
        </div>
      </BaseCard>
    );
  }

  return (
    <BaseCard>
      <div className="space-y-4 -mx-7 md:mx-0">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 py-4 border-b border-gray-200 px-6 md:px-0">
          <h2 className="text-xl font-bold text-gray-900">Community Members</h2>
          <Tooltip text="Coming soon - member invitations will be available in a future update">
            <Button variant="filled" size="small" onClick={() => {}} icon={<PlusIcon className="h-5 w-5" />} disabled={true}>
              Invite Member
            </Button>
          </Tooltip>
        </div>

        <div className="px-5 md:px-0">
          <div className="p-4 text-sm text-blue-700 bg-blue-50 rounded-lg">
            <p>
              <strong>Community Member Management:</strong> Users can join this community directly from the community page.
              You can manage member roles and remove members as needed.
            </p>
          </div>
        </div>

        {error && (
          <div className="px-5 md:px-0">
            <div className="p-4 text-sm text-red-700 bg-red-100 rounded-lg">{error}</div>
          </div>
        )}

        <DataTable
          columns={columns}
          data={paginatedMembers}
          loading={loading}
          error={error}
          emptyMessage={searchTerm ? 'No members match your search.' : 'No members found.'}
          totalItemsCount={members.length}
          pagination={{
            currentPage: tableState.currentPage,
            totalPages,
            totalItems,
            itemsPerPage: tableState.itemsPerPage,
            onPageChange: (page) => updateTableState({ currentPage: page }),
            onItemsPerPageChange: (size) => updateTableState({ itemsPerPage: size, currentPage: 1 })
          }}
          selection={{
            selectedItems: tableState.selectedItems,
            onSelectAll: (checked) => {
              const currentPageIds = paginatedMembers.map(member => member.community_member_id);
              const allCurrentPageSelected = currentPageIds.every(id => tableState.selectedItems.has(id));
              const hasSelectionsAcrossPages = tableState.selectedItems.size > 0 && 
                Array.from(tableState.selectedItems).some(id => !currentPageIds.includes(id));
              
              if (!checked && hasSelectionsAcrossPages) {
                // If unchecking and there are selections across multiple pages, deselect ALL
                updateTableState({ selectedItems: new Set() });
              } else if (checked && allCurrentPageSelected && members.length > paginatedMembers.length) {
                // If all current page items are already selected and user clicks "Select All",
                // select ALL members across all pages
                const allMemberIds = members.map(member => member.community_member_id);
                updateTableState({ selectedItems: new Set(allMemberIds) });
              } else {
                // Normal behavior: select/deselect current page items
                const newSelected = new Set(tableState.selectedItems);
                currentPageIds.forEach(id => checked ? newSelected.add(id) : newSelected.delete(id));
                updateTableState({ selectedItems: newSelected });
              }
            },
            onSelectOne: (memberId, checked) => {
              const newSelected = new Set(tableState.selectedItems);
              checked ? newSelected.add(memberId) : newSelected.delete(memberId);
              updateTableState({ selectedItems: newSelected });
            },
            getItemId: (member) => member.community_member_id,
            isSelectAll: isAllCurrentPageSelected,
            isIndeterminate
          }}
          search={{
            value: searchTerm,
            onChange: (value) => {
              setSearchTerm(value || '');
              updateTableState({ currentPage: 1 });
            },
            placeholder: "Search members..."
          }}
          bulkActions={{
            actions: BULK_ACTIONS,
            selectedAction: bulkAction.type,
            onActionChange: (actionId) => setBulkAction({ type: actionId as typeof bulkAction.type }),
            onActionExecute: handleBulkActionExecute,
            secondaryOptions: ROLE_OPTIONS,
            selectedSecondaryOption: bulkAction.roleChange,
            onSecondaryOptionChange: (optionId) => setBulkAction({ ...bulkAction, roleChange: optionId }),
            canPerformActions: canManageMembers,
            restrictionMessage: !canManageMembers ? 'You do not have permission to manage community members.' : undefined,
            requiresSecondarySelection: new Set(['change_role']),
            customActionButtons: {
              'remove_members': {
                text: 'Remove Selected',
                variant: 'outline',
                color: 'danger'
              },
              'export_csv': {
                text: 'Export',
                variant: 'outline',
                icon: exportStatus === 'preparing' 
                  ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                  : <ArrowDownTrayIcon className="h-4 w-4" />,
                loading: exportStatus === 'preparing',
                loadingText: 'Preparing...',
                successText: exportStatus === 'ready' ? 'Downloaded!' : undefined
              }
            }
          }}
          sort={{
            field: tableState.sortField,
            direction: tableState.sortDirection,
            onSort: (field) => {
              updateTableState({
                sortField: field,
                sortDirection: tableState.sortField === field && tableState.sortDirection === 'asc' ? 'desc' : 'asc'
              });
            }
          }}
          gridCols={gridCols}
        />

        <ModalDialog
          isOpen={!!confirmingAction}
          onClose={() => setConfirmingAction(null)}
          onPrimaryButtonClick={() => {
            if (!confirmingAction) return;
            
            if (confirmingAction.action === 'change_role' && confirmingAction.newRole) {
              if (confirmingAction.memberIds.size === 1) {
                const memberId = Array.from(confirmingAction.memberIds)[0];
                updateMemberRole(memberId, confirmingAction.newRole as CommunityMemberRole);
              } else {
                confirmBulkAction();
              }
            } else {
              confirmBulkAction();
            }
          }}
          onSecondaryButtonClick={() => setConfirmingAction(null)}
          title={confirmingAction?.action === 'change_role' ? 'Change Role' : 'Remove Members'}
          description={
            confirmingAction?.action === 'change_role'
              ? `This will change the role of ${confirmingAction?.memberIds.size} member(s) to "${confirmingAction?.newRole}".`
              : `This will remove ${confirmingAction?.memberIds.size} member(s) from the community.`
          }
          primaryButtonText="Confirm"
          icon={confirmingAction?.action === 'remove' ? 'warning' : 'info'}
          primaryButtonColor={confirmingAction?.action === 'remove' ? 'danger' : 'primary'}
        />
      </div>
    </BaseCard>
  );
}; 